import { AccessAlarm, Edit } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button } from "@mui/material";
import { GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import { Contact, ContactType } from "@repo/server-api";
import { useUserRoles, RuleComponent  } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import ConformationDialog from "@repo/legacy/components/shared/dialogs/Conformation";
import DialogContact from "@repo/legacy/components/shared/dialogs/DialogContact";
import List from "@repo/legacy/components/shared/lists/List";

import { useAllContactsFullDataSource } from "../../components/shared/datasources/AllContactsFullDataSource";

const Contractors = (props: { onlyEmails?: boolean; timeLabels?: boolean }) => {
	const { t } = useTranslation();

	const navigate = useNavigate();
	const [openDialog, setOpenDialog] = useState<boolean>(false);

	const roles = useUserRoles();

	const allContractorsDataSource = useAllContactsFullDataSource();

	useEffect(() => {
		if (
			!roles.isMultiContractor &&
			roles.contractorId &&
			!roles.roles.includes("tenant_administrator")
		) {
			if (!props.timeLabels && !props.onlyEmails)
				navigate(`/contractors/${encodeURIComponent(roles.contractorId)}`);
			else if (props.timeLabels)
				navigate(`/time_management/time_labels/${encodeURIComponent(roles.contractorId)}`);
			else if (props.onlyEmails)
				navigate(`/document_generator/templates/${encodeURIComponent(roles.contractorId)}`);
		}
	}, [roles, navigate, props.onlyEmails, props.timeLabels]);

	const columns: GridColDef<Contact>[] = useMemo(
		() => [
			{
				field: "displayName",
				headerName: t("name"),
				width: 150,
				flex: 1,
				renderCell(params: GridCellParams) {
					return params.row.displayName;
				},
			},
			{
				field: "contactNumber",
				headerName: t("contact-number"),
				width: 150,
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				align: "center",
				headerAlign: "center",
				width: 200,
				renderCell: (params: GridCellParams) => {
					const [openDeleteConformation, setOpenDeleteConformation] = useState<boolean>(false);

					const handleYes = (id: string) => {
						if (allContractorsDataSource.deleteData) allContractorsDataSource.deleteData(id, true);
						setOpenDeleteConformation(false);
					};
					const openDelete = () => {
						setOpenDeleteConformation(true);
					};
					const openEdit = (contact: Contact) => {
						navigate(`/contractors/${encodeURIComponent(contact.id)}`);
					};

					return (
						<div>
							{!props.onlyEmails && !props.timeLabels && (
								<>
									<Button data-testid="edit-button" onClick={() => openEdit(params.row)}>
										<Edit />
									</Button>
									<Button data-testid="delete-button" onClick={() => openDelete()}>
										<DeleteIcon />
									</Button>
									<ConformationDialog
										text={t("contractors.deleteConformation")}
										setOpenDeleteConformation={setOpenDeleteConformation}
										openPopup={openDeleteConformation}
										handleYes={() => handleYes(params.id.toString())}
									/>
								</>
							)}
							{!props.timeLabels ? (
								// <Button
								// 	onClick={() =>
								// 		navigate(
								// 			`/document_generator/templates/${encodeURIComponent(params.id.toString())}`,
								// 		)
								// 	}
								// >
								// 	<Email />
								// </Button>
								<></>
							) : (
								<Button
									onClick={() =>
										navigate(
											`/time_management/time_labels/${encodeURIComponent(params.id.toString())}`,
										)
									}>
									<AccessAlarm />
								</Button>
							)}
						</div>
					);
				},
			},
		],

		[t],
	);

	return (
		<>
			<DialogContact
				type={ContactType.Contractor}
				refreshData={(id: string) => {
					navigate(`/contractors/${encodeURIComponent(id)}`);
				}}
				setOpen={setOpenDialog}
				open={openDialog}
				contact={null}
			/>

			<h3 className="purpleHeader">{t("contractors")}</h3>
			<List
				columns={columns as GridCellParams<Contact>[]}
				dataSource={allContractorsDataSource}
				showDeletedFilter={true}
				contactTypes={[ContactType.Contractor, ContactType.Company]}>
				<RuleComponent rule={() => roles.roles.includes("tenant_administrator")}>
					<Button
						data-testid="add-button"
						onClick={() => setOpenDialog(true)}
						className="right blueButton"
						color="secondary">
						{t("contractor.create").toLocaleUpperCase()} <AddIcon />
					</Button>
				</RuleComponent>
			</List>
		</>
	);
};

export default Contractors;
